import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, GlobalStyles, Typography } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import ImageWithAlign from './ImageWithAlign';
import { NodeSelection } from 'prosemirror-state';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: React.ReactNode;
  maxChars?: number;
  minHeight?: number;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  showCharacterCount?: boolean;
  uploadImage: (file: File) => Promise<string>; // must return URL
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label = 'Description',
  maxChars = 500,
  minHeight = 180,
  placeholder = 'Start typing...',
  disabled,
  error,
  helperText,
  showCharacterCount = true,
  uploadImage,
}) => {
  const [, force] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit,
      TextStyle,
      ImageWithAlign.configure({ inline: false, allowBase64: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (maxChars && editor.getText().length > maxChars) {
        // revert if exceeded
        editor.commands.undo();
        return;
      }
      onChange(html);
    },
  });

  // Keep external value in sync (when parent sets new value e.g. on load)
  useEffect(() => {
    if (editor && value && value !== editor.getHTML()) {
      // Updated for new Tiptap typing: second argument is an options object
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  // Force toolbar rerender on selection changes
  useEffect(() => {
    if (!editor) return;
    const rerender = () => force(v => v + 1);
    editor.on('selectionUpdate', rerender);
    editor.on('transaction', rerender);
    return () => {
      editor.off('selectionUpdate', rerender);
      editor.off('transaction', rerender);
    };
  }, [editor]);

  const pickImage = () => fileInputRef.current?.click();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) return;
    if (file.size > 2 * 1024 * 1024) {
      setImageError('Image size must be less than 2MB');
      e.target.value = '';
      return;
    } else {
      setImageError(null);
    }
    try {
      const url = await uploadImage(file);
      editor?.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch {
      // ignore (parent can toast)
    } finally {
      e.target.value = '';
    }
  };

  const isImageSelected =
    editor?.state.selection instanceof NodeSelection &&
    (editor.state.selection as NodeSelection).node.type.name === 'image';

  const currentImageAlign = isImageSelected
    ? (editor!.state.selection as NodeSelection).node.attrs.align || null
    : null;

  const setImageAlign = (align: string | null) => {
    if (!isImageSelected) return;
    const sel = editor!.state.selection as NodeSelection;
    editor!
      .chain()
      .focus()
      .setNodeSelection(sel.from)
      .updateAttributes('image', { align })
      .run();
  };

  const charCount = editor?.getText().length || 0;

  return (
    <Box>
      <GlobalStyles
        styles={{
          '.ProseMirror': {
            outline: 'none',
          },
          '.ProseMirror img': {
            maxWidth: '100%',
            height: 'auto',
            cursor: 'pointer',
            display: 'block',
          },
          '.ProseMirror img[data-align="left"]': {
            margin: '4px 16px 8px 0',
            float: 'left',
            clear: 'both',
          },
            '.ProseMirror img[data-align="right"]': {
            margin: '4px 0 8px 16px',
            float: 'right',
            clear: 'both',
          },
          '.ProseMirror img[data-align="center"]': {
            margin: '8px auto',
            float: 'none',
          },
          '.ProseMirror-selectednode': {
            outline: '2px solid #673ab7',
            outlineOffset: '2px',
          },
          '.ProseMirror p::after': {
            content: '""',
            display: 'block',
            clear: 'both',
          },
          '.rt-editor-placeholder:is(:not(.has-content))::before': {
            content: 'attr(data-placeholder)',
            color: '#888',
            pointerEvents: 'none',
            position: 'absolute',
            left: 12,
            top: 8,
            fontSize: '0.85rem',
          },
        }}
      />
      {label && (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}
        >
          {label}
        </Typography>
      )}
      {/* Toolbar */}
      <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive('bold')) {
              editor.chain().focus().unsetBold().run();
            } else {
              editor?.chain().focus().toggleBold().run();
            }
          }}
          variant={editor?.isActive('bold') ? 'contained' : 'outlined'}
        >
          Bold
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive('italic')) {
              editor.chain().focus().unsetItalic().run();
            } else {
              editor?.chain().focus().toggleItalic().run();
            }
          }}
          variant={editor?.isActive('italic') ? 'contained' : 'outlined'}
        >
          Italic
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive('strike')) {
              editor.chain().focus().unsetStrike().run();
            } else {
              editor?.chain().focus().toggleStrike().run();
            }
          }}
          variant={editor?.isActive('strike') ? 'contained' : 'outlined'}
        >
          Strike
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive('heading', { level: 1 })) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor?.chain().focus().toggleHeading({ level: 1 }).run();
            }
          }}
          variant={editor?.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
        >
          H1
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive('heading', { level: 2 })) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor?.chain().focus().toggleHeading({ level: 2 }).run();
            }
          }}
          variant={editor?.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
        >
          H2
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive('bulletList')) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor?.chain().focus().toggleBulletList().run();
            }
          }}
          variant={editor?.isActive('bulletList') ? 'contained' : 'outlined'}
        >
          Bullet
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive('orderedList')) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor?.chain().focus().toggleOrderedList().run();
            }
          }}
          variant={editor?.isActive('orderedList') ? 'contained' : 'outlined'}
        >
          Numbered
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive({ textAlign: 'left' })) {
              editor.chain().focus().setTextAlign('start').run();
            } else {
              editor?.chain().focus().setTextAlign('left').run();
            }
          }}
          variant={editor?.isActive({ textAlign: 'left' }) ? 'contained' : 'outlined'}
        >
          L
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive({ textAlign: 'center' })) {
              editor.chain().focus().setTextAlign('start').run();
            } else {
              editor?.chain().focus().setTextAlign('center').run();
            }
          }}
          variant={editor?.isActive({ textAlign: 'center' }) ? 'contained' : 'outlined'}
        >
          C
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive({ textAlign: 'right' })) {
              editor.chain().focus().setTextAlign('start').run();
            } else {
              editor?.chain().focus().setTextAlign('right').run();
            }
          }}
          variant={editor?.isActive({ textAlign: 'right' }) ? 'contained' : 'outlined'}
        >
          R
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (editor?.isActive({ textAlign: 'justify' })) {
              editor.chain().focus().setTextAlign('start').run();
            } else {
              editor?.chain().focus().setTextAlign('justify').run();
            }
          }}
          variant={editor?.isActive({ textAlign: 'justify' }) ? 'contained' : 'outlined'}
        >
          J
        </Button>
        <Button size="small" onClick={pickImage} variant="outlined">
          Image
        </Button>
        <Button
          size="small"
            disabled={!isImageSelected}
          onClick={() => setImageAlign('left')}
          variant={isImageSelected && currentImageAlign === 'left' ? 'contained' : 'outlined'}
        >
          Img L
        </Button>
        <Button
          size="small"
          disabled={!isImageSelected}
          onClick={() => setImageAlign('center')}
          variant={isImageSelected && currentImageAlign === 'center' ? 'contained' : 'outlined'}
        >
          Img C
        </Button>
        <Button
          size="small"
          disabled={!isImageSelected}
          onClick={() => setImageAlign('right')}
          variant={isImageSelected && currentImageAlign === 'right' ? 'contained' : 'outlined'}
        >
          Img R
        </Button>
        <Button
          size="small"
          disabled={!isImageSelected}
          onClick={() => setImageAlign(null)}
          variant={isImageSelected && !currentImageAlign ? 'contained' : 'outlined'}
        >
          Img None
        </Button>
        <Button
          size="small"
          onClick={() => editor?.chain().focus().undo().run()}
          disabled={!editor?.can().undo()}
        >
          Undo
        </Button>
        <Button
          size="small"
          onClick={() => editor?.chain().focus().redo().run()}
          disabled={!editor?.can().redo()}
        >
          Redo
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </Box>

      {/* Editor area */}
      <Box
        sx={{
          border: theme =>
            `1px solid ${error ? theme.palette.error.main : '#e0e0e0'}`,
          borderRadius: 2,
          p: 1,
          minHeight,
          bgcolor: disabled ? '#f5f5f5' : '#fafafa',
          position: 'relative',
          '&:hover': { borderColor: error ? 'error.main' : 'primary.main' },
        }}
        className={
          'rt-editor-root ' +
          (editor && editor.getText().trim() ? 'has-content' : '')
        }
      >
        <div
          className={
            'rt-editor-placeholder ' +
            (editor && editor.getText().trim() ? 'has-content' : '')
          }
          data-placeholder={placeholder}
          style={{ position: 'relative' }}
        >
          <EditorContent editor={editor} />
        </div>
        {imageError && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {imageError}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          mt: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {(charCount > maxChars) ? (
          <Typography
            variant="caption"
            color={'error.main'}
          >
            Product description cannot exceed {maxChars} characters
          </Typography>
        ) : (
          helperText && (
            <Typography
              variant="caption"
              color={error ? 'error.main' : 'text.secondary'}
            >
              {helperText}
            </Typography>
          )
        )}
        {showCharacterCount && (
          <Typography
            variant="caption"
            color={charCount > maxChars ? 'error.main' : 'text.secondary'}
          >
            {charCount}/{maxChars} characters
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RichTextEditor;